import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { FileText, Plus, Edit, Trash2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminPolicies() {
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState(null);
  const [formData, setFormData] = useState({
    policy_type: '',
    policy_title: '',
    policy_content: ''
  });

  useEffect(() => {
    fetchPolicies();
  }, []);

  const fetchPolicies = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/policies`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPolicies(response.data);
    } catch (error) {
      toast.error('Failed to load policies');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      if (editingPolicy) {
        await axios.put(`${API}/policies/${editingPolicy.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Policy updated successfully');
      } else {
        await axios.post(`${API}/policies`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Policy created successfully');
      }
      fetchPolicies();
      setTimeout(() => {
        setDialogOpen(false);
        setEditingPolicy(null);
        setFormData({ policy_type: '', policy_title: '', policy_content: '' });
      }, 100);
    } catch (error) {
      toast.error('Failed to save policy');
    }
  };

  const handleEdit = (policy) => {
    setEditingPolicy(policy);
    setFormData({
      policy_type: policy.policy_type,
      policy_title: policy.policy_title,
      policy_content: policy.policy_content
    });
    setDialogOpen(true);
  };

  const handleDelete = async (policyId) => {
    if (!window.confirm('Are you sure you want to delete this policy?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/policies/${policyId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Policy deleted successfully');
      fetchPolicies();
    } catch (error) {
      toast.error('Failed to delete policy');
    }
  };

  const handleDialogClose = (open) => {
    setDialogOpen(open);
    if (!open) {
      setEditingPolicy(null);
      setFormData({ policy_type: '', policy_title: '', policy_content: '' });
    }
  };

  return (
    <Layout>
      <div className="space-y-8" data-testid="admin-policies-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold tracking-tight">Company Policies</h1>
            <p className="text-muted-foreground mt-2">Manage company policies and guidelines</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={handleDialogClose}>
            <DialogTrigger asChild>
              <Button data-testid="add-policy-button">
                <Plus className="mr-2 h-4 w-4" />
                Add Policy
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingPolicy ? 'Edit Policy' : 'Create New Policy'}</DialogTitle>
                <DialogDescription>
                  {editingPolicy ? 'Update the policy details' : 'Add a new company policy'}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="policy_type">Policy Type</Label>
                  <Input
                    id="policy_type"
                    value={formData.policy_type}
                    onChange={(e) => setFormData({...formData, policy_type: e.target.value})}
                    placeholder="e.g., Leave Policy, Reporting Policy, Code of Conduct"
                    required
                    data-testid="input-policy-type"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="policy_title">Policy Title</Label>
                  <Input
                    id="policy_title"
                    value={formData.policy_title}
                    onChange={(e) => setFormData({...formData, policy_title: e.target.value})}
                    placeholder="e.g., Annual Leave Entitlement"
                    required
                    data-testid="input-policy-title"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="policy_content">Policy Content</Label>
                  <Textarea
                    id="policy_content"
                    value={formData.policy_content}
                    onChange={(e) => setFormData({...formData, policy_content: e.target.value})}
                    placeholder="Enter the complete policy details..."
                    rows={10}
                    required
                    data-testid="textarea-policy-content"
                    className="min-h-[200px]"
                  />
                </div>
                <Button type="submit" className="w-full" data-testid="submit-policy-button">
                  {editingPolicy ? 'Update Policy' : 'Create Policy'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <Card data-testid="policies-table-card">
          <CardHeader>
            <CardTitle className="flex items-center">
              <FileText className="mr-2 h-5 w-5" />
              All Policies
            </CardTitle>
            <CardDescription>Complete list of company policies</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">Loading...</div>
            ) : policies.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No policies found. Create your first policy.
              </div>
            ) : (
              <div className="w-full overflow-auto rounded-lg border border-border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Policy Type</TableHead>
                      <TableHead>Title</TableHead>
                      <TableHead>Last Updated</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {policies.map((policy) => (
                      <TableRow key={policy.id} data-testid="policy-row">
                        <TableCell>
                          <span className="px-2 py-1 bg-secondary rounded-full text-sm">
                            {policy.policy_type}
                          </span>
                        </TableCell>
                        <TableCell className="font-medium">{policy.policy_title}</TableCell>
                        <TableCell>{new Date(policy.updated_at).toLocaleDateString()}</TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleEdit(policy)}
                              data-testid="edit-policy-button"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={() => handleDelete(policy.id)}
                              data-testid="delete-policy-button"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
